import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
const VideoModal = ({
    open,
    onClose,
    videoSrc,
    posterSrc,
    ctaLabel = 'Continue',
    onComplete,
}) => {
    const videoRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [elapsed, setElapsed] = useState(0);
    const [duration, setDuration] = useState(0);
    const [buffered, setBuffered] = useState(0);
    const [finished, setFinished] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const controlsTimer = useRef(null);

    const progress = duration > 0 ? (elapsed / duration) * 100 : 0;
    const bufferedPct = duration > 0 ? (buffered / duration) * 100 : 0;

    // Reset on open/close
    useEffect(() => {
        if (open) {
            setElapsed(0);
            setPlaying(false);
            setFinished(false);
            setShowControls(true);
            if (videoRef.current) videoRef.current.currentTime = 0;
        } else {
            videoRef.current?.pause();
            setPlaying(false);
        }
    }, [open]);

    // Auto-hide controls after 3s of playing
    const resetControlsTimer = useCallback(() => {
        setShowControls(true);
        clearTimeout(controlsTimer.current);
        controlsTimer.current = setTimeout(() => {
            if (playing) setShowControls(false);
        }, 3000);
    }, [playing]);

    useEffect(() => {
        if (playing) resetControlsTimer();
        else {
            clearTimeout(controlsTimer.current);
            setShowControls(true);
        }
        return () => clearTimeout(controlsTimer.current);
    }, [playing, resetControlsTimer]);

    const handleTimeUpdate = useCallback(() => {
        const v = videoRef.current;
        if (!v) return;
        setElapsed(v.currentTime);
        if (v.buffered.length > 0) {
            setBuffered(v.buffered.end(v.buffered.length - 1));
        }
    }, []);

    const handleLoadedMetadata = useCallback(() => {
        setDuration(videoRef.current?.duration || 0);
    }, []);

    const handleEnded = useCallback(() => {
        setPlaying(false);
        setFinished(true);
        setShowControls(true);
        onComplete?.();
    }, [onComplete]);

    const togglePlay = useCallback(() => {
        const v = videoRef.current;
        if (!v || finished) return;
        resetControlsTimer();
        if (playing) {
            v.pause();
            setPlaying(false);
        } else {
            v.play();
            setPlaying(true);
        }
    }, [playing, finished, resetControlsTimer]);

    const handleTap = useCallback(() => {
        if (!finished) {
            if (showControls) {
                togglePlay();
            } else {
                resetControlsTimer();
            }
        }
    }, [finished, showControls, togglePlay, resetControlsTimer]);

    const formatTime = (s) => {
        if (!s || isNaN(s)) return '0:00';
        const m = Math.floor(s / 60);
        return `${m}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[9999] bg-black flex flex-col"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                >
                    {/* Video fills the full screen */}
                    <div
                        className="relative flex-1 flex items-center justify-center bg-black overflow-hidden"
                        onClick={handleTap}
                    >
                        <video
                            ref={videoRef}
                            src={videoSrc}
                            poster={posterSrc}
                            playsInline
                            preload="metadata"
                            onTimeUpdate={handleTimeUpdate}
                            onLoadedMetadata={handleLoadedMetadata}
                            onEnded={handleEnded}
                            controls={false}
                            controlsList="nodownload nofullscreen noremoteplayback"
                            disablePictureInPicture
                            onContextMenu={(e) => e.preventDefault()}
                            onSeeking={() => {
                                // Prevent seeking ahead
                                if (videoRef.current && videoRef.current.currentTime > elapsed + 0.5) {
                                    videoRef.current.currentTime = elapsed;
                                }
                            }}
                            className="w-full h-full object-cover"
                        />

                        {/* Play / Pause tap overlay */}
                        <AnimatePresence>
                            {showControls && !finished && (
                                <motion.div
                                    className="absolute inset-0 flex items-center justify-center"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                    // Don't block click events
                                    style={{ pointerEvents: 'none' }}
                                >
                                    <div className="h-16 w-16 flex items-center justify-center rounded-full bg-black/40 border border-white/20 backdrop-blur-sm">
                                        {playing ? (
                                            // Pause icon
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                                                <rect x="5" y="3" width="4" height="18" rx="1" />
                                                <rect x="15" y="3" width="4" height="18" rx="1" />
                                            </svg>
                                        ) : (
                                            // Play icon
                                            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                                                <polygon points="6,3 21,12 6,21" />
                                            </svg>
                                        )}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* "Watch to continue" top label */}
                        <AnimatePresence>
                            {showControls && !finished && (
                                <motion.div
                                    className="absolute top-0 inset-x-0 pt-12 pb-6 px-5 bg-gradient-to-b from-black/60 to-transparent pointer-events-none"
                                    initial={{ opacity: 0, y: -8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -8 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <div className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#ff4da6] animate-pulse" />
                                        <span className="text-[11px] font-semibold uppercase tracking-widest text-white/80">
                                            Watch to continue
                                        </span>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* Bottom: progress bar + time */}
                        <AnimatePresence>
                            {(showControls || finished) && (
                                <motion.div
                                    className="absolute bottom-0 inset-x-0 px-4 pb-5 pt-12 bg-gradient-to-t from-black/70 to-transparent"
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 8 }}
                                    transition={{ duration: 0.2 }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {/* Segment strip */}
                                    <SegmentStrip elapsed={elapsed} duration={duration} segments={5} />

                                    {/* Scrub bar */}
                                    <div className="relative h-[3px] w-full rounded-full bg-white/20 mt-2 overflow-hidden">
                                        {/* Buffered */}
                                        <div
                                            className="absolute inset-y-0 left-0 rounded-full bg-white/25"
                                            style={{ width: `${bufferedPct}%` }}
                                        />
                                        {/* Played */}
                                        <div
                                            className="absolute inset-y-0 left-0 rounded-full bg-[#ff4da6] transition-[width] duration-150 ease-linear"
                                            style={{ width: `${progress}%` }}
                                        />
                                    </div>

                                    {/* Time */}
                                    <div className="mt-2 flex items-center justify-between">
                                        <span className="text-[11px] tabular-nums text-white/55">
                                            {formatTime(elapsed)}
                                        </span>
                                        <span className="text-[11px] tabular-nums text-white/55">
                                            {formatTime(duration)}
                                        </span>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* CTA — slides up after video ends */}
                    <AnimatePresence>
                        {finished && (
                            <motion.div
                                className="absolute bottom-0 inset-x-0 px-5 pb-10 pt-6 bg-gradient-to-t from-black via-black/90 to-transparent"
                                initial={{ y: 80, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: 80, opacity: 0 }}
                                transition={{ type: 'spring', stiffness: 260, damping: 22 }}
                            >
                                <button
                                    onClick={onClose}
                                    className="w-full rounded-full bg-[#ff4da6] py-4 text-base font-semibold text-white shadow-[0_8px_32px_rgba(255,77,166,0.4)] active:scale-[0.98] transition-transform"
                                >
                                    {ctaLabel}
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/** Segmented chapter-strip above the scrub bar */
const SegmentStrip = ({ elapsed, duration, segments = 5 }) => {
    if (!duration) return null;
    const secPerSeg = duration / segments;
    return (
        <div className="flex gap-1">
            {Array.from({ length: segments }).map((_, i) => {
                const segStart = i * secPerSeg;
                const segEnd = (i + 1) * secPerSeg;
                let fillPct = 0;
                if (elapsed >= segEnd) fillPct = 100;
                else if (elapsed > segStart) fillPct = ((elapsed - segStart) / secPerSeg) * 100;
                return (
                    <div key={i} className="flex-1 h-[2px] rounded-full bg-white/20 overflow-hidden">
                        <div
                            className="h-full rounded-full bg-[#ff4da6] transition-[width] duration-150 ease-linear"
                            style={{ width: `${fillPct}%` }}
                        />
                    </div>
                );
            })}
        </div>
    );
};

export default VideoModal;